import {useRef} from "react";
import {useAppSelector} from "hooks/useAppSelector";
import {getUserMoneyType, getUserTurnover, setBalance} from "app/store/slices/user";
import {MoneyType} from "app/types/common";
import {LatestBetOrder, setWinAmount, updateLatestBets, updateHighRollers} from "app/store/slices/game";
import {getUserMessageCountList} from "app/store/slices/message";
import {setIsDeposit} from "app/store/slices/setting";
import pkg from 'lodash';
import { useDispatch } from "react-redux";
const {cloneDeep, divide} = pkg;

type PlatformMessageBalanceParam = {
    type: MoneyType;
    amount: number;
    delay: number;
    mode: number;
    locked_amount: number;
    unlocked_amount: number;
};

const useMessage = () => {
    const dispatch = useDispatch();
    const balanceList = useAppSelector((state) => state.user.balance);
    const moneyType = useAppSelector(getUserMoneyType);
    const timer = useRef<NodeJS.Timeout | undefined>(undefined);

    const handleBalanceChange = (param: PlatformMessageBalanceParam) => {
        if (param.type === moneyType && (param.mode === 1 || param.mode === 4 || param.mode === 6)) {
            const old = balanceList.find((x) => x.type === moneyType);
            if (old) {
                const diff = param.amount * 100000000 - old.amount * 100000000;
                if (diff > 0) {
                    dispatch(setWinAmount(divide(diff, 100000000)));
                }
            }
        }

        const tempList = cloneDeep(balanceList);
        tempList.forEach((e) => {
            if (e.type === param.type) {
                if (param.mode === 1 || param.mode === 4 || param.mode === 6) {
                    e.amount = param.amount;
                }
                if (param.mode === 2 || param.mode === 4 || param.mode === 5 || param.mode === 6) {
                    e.locked_amount = param.locked_amount;
                }
                if (param.mode === 3 || param.mode === 5 || param.mode === 6) {
                    e.unlocked_amount = param.unlocked_amount;
                }
            }
        });
        dispatch(getUserTurnover());
        dispatch(setBalance(tempList));
    };

    const handleReceiveMsg = (result: any) => {
        if (!result) {
            return;
        }

        // Helper function to safely parse param - handles both JSON strings and objects
        const safeParseParam = (param: any) => {
            if (typeof param === 'string') {
                try {
                    return JSON.parse(param);
                } catch (e) {
                    console.error('Failed to parse param:', e);
                    return null;
                }
            }
            return param;
        };

        try {
            switch (result.type) {
                case 1:
                    dispatch(getUserMessageCountList());
                    break;
                // Balance change
                case 3:
                    {
                        const param: PlatformMessageBalanceParam = safeParseParam(result.param);
                        if (!param) {
                            console.error('Invalid balance update param');
                            break;
                        }

                        timer.current && clearTimeout(timer.current);
                        if (param.delay > 0) {
                            timer.current = setTimeout(() => {
                                handleBalanceChange(param);
                            }, param.delay);
                            break;
                        }

                        handleBalanceChange(param);
                    }
                    break;
                // Recharge result
                case 4:
                    {
                        const rechargeParam: {result: number; amount: number; type: number} = safeParseParam(result.param);
                        if (!rechargeParam) {
                            console.error('Invalid recharge param');
                            break;
                        }
                        if (rechargeParam.result === 1) {
                            dispatch(setIsDeposit(true));
                        }
                    }
                    break;
                case 6:
                    {
                        const latestBet: LatestBetOrder = safeParseParam(result.param);
                        if (!latestBet) {
                            console.error('Invalid latest bet param');
                            break;
                        }
                        dispatch(updateLatestBets(latestBet));
                    }
                    break;

                case 7:
                    {
                        const highRollers: LatestBetOrder = safeParseParam(result.param);
                        if (!highRollers) {
                            console.error('Invalid high rollers param');
                            break;
                        }
                        dispatch(updateHighRollers(highRollers));
                    }
                    break;
                default:
                    break;
            }
        } catch (error) {
            // empty
        }
    };

    return {handleReceiveMsg};
};

export default useMessage;
